// OMD-222: `?message_id=`/`?item_id=` 딥링크로 특정 메일에 직접 이동할 때의
// 대상 메일/폴더 특정 로직. main/index.tsx의 moveByMessageId/moveByEmailId에서 사용한다.

export type MoveFailureReason = 'not-found' | 'load-error'

export interface MoveTargetEmail {
  data: { mailboxIds?: Record<string, boolean> | null }
  getId: () => string
}

export interface ThreadByMessageIdResult {
  // getThreadByMessageId가 반환하는 스레드 전체 메일 목록. resolveMoveTarget은
  // titleEmail만 사용하므로 이동 대상 특정에는 쓰이지 않는다(폴백 금지).
  emailObjList?: MoveTargetEmail[] | null
  titleEmail: MoveTargetEmail | null
}

export type MoveTarget =
  | { email: MoveTargetEmail; mailbox: unknown }
  | { failure: MoveFailureReason }

type GetMailboxById = (mailboxId: string) => unknown

// 메일이 속한 폴더들 중 로컬 dataManager에 로드된 첫 폴더를 반환한다.
// (기존 `.some(() => { ...; return true })` 패턴은 첫 mailboxId만 보고 종료해
// 후순위 폴더만 유효한 메일의 이동이 실패했다.)
export function findLoadedMailbox(
  mailboxIds: Record<string, boolean> | null | undefined,
  getMailboxById: GetMailboxById,
): unknown | null {
  if (mailboxIds == null) {
    return null
  }
  for (const mailboxId of Object.keys(mailboxIds)) {
    const mailbox = getMailboxById(mailboxId)
    if (mailbox != null) {
      return mailbox
    }
  }
  return null
}

// getThreadByMessageId 결과에서 이동 대상 메일과 폴더를 특정한다.
// titleEmail(= message-id로 실제 매치된 메일)만 사용한다. emailObjList[0]은 스레드의
// 첫 메일이라 매치 메일과 다른 폴더(예: Sent vs Inbox)일 수 있어, titleEmail을 특정하지
// 못한 경우 emailObjList[0]로 폴백하면 원래 버그(엉뚱한 폴더 열림)를 재현한다.
// 따라서 titleEmail이 없으면 추측하지 않고 not-found로 처리한다.
export function resolveMoveTarget(
  res: ThreadByMessageIdResult | null | undefined,
  getMailboxById: GetMailboxById,
): MoveTarget {
  if (res == null) {
    return { failure: 'load-error' }
  }

  const email = res.titleEmail ?? null
  if (email == null) {
    return { failure: 'not-found' }
  }

  const mailbox = findLoadedMailbox(email.data?.mailboxIds, getMailboxById)
  if (mailbox == null) {
    return { failure: 'not-found' }
  }

  return { email, mailbox }
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError'
}

// JMAP 요청이 60초 hard timeout(AbortError)으로 중단된 경우에 한해 1회 재시도한다.
// 서버/JMAP 메서드 에러는 재시도해도 같은 결과이므로 그대로 전파한다.
export async function requestWithAbortRetry<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request()
  } catch (err) {
    if (isAbortError(err)) {
      return await request()
    }
    throw err
  }
}
